import {
  BigPictureClient,
  EventContexts,
  Event,
  Events,
  Fields,
  Versions,
} from '@vimeo/bigpicture-client';

export function sendBpEventWithContexts<E extends Events, V extends Versions<E>>(
  eventName: E,
  contexts: EventContexts[E],
  eventSchemaVersion: V = -1 as V,
  additionalFields: Fields<E, V> = {} as Fields<E, V>,
): void {
  const event = new Event(eventName, eventSchemaVersion, additionalFields);
  BigPictureClient.sendEventWithContexts(event, contexts);
}

/**
 * Creates an object with null values for the given keys.
 *
 * @example
 * ```
 * createNullObject(['a', 'b', 'c']) // {a: null, b: null, c: null}
 * ```
 *
 * Note: This function does not provide excess property checks. For instance,
 * If you do something like this...
 * ```
 * type ClickFields = {
 *   a: string | null;
 *   b: string | null;
 * }
 *
 * const clickFields = createNullObject<ClickFields>(['a', 'b', 'c']);
 * ```
 * ...you will not receive an error for the excess property `c`.
 *
 * However, you will receive an error if either `a` or `b` are not included in the array.
 *
 * If a bright mind comes along and can figure out how to do this with excess property checks,
 * I'll forever be indebted.
 */
export function createNullObject<T extends string>(strings: T[]): Record<T, null> {
  const nullObject: Record<T, null> = {} as Record<T, null>;

  for (const str of strings) {
    nullObject[str] = null;
  }

  return nullObject;
}

// T = type to make Partial
// K = keys of T to make Required
// e.g, PartialWithRequired<{a: string, b: string}, 'a'> = {a: string, b?: string}
export type PartialWithRequired<T, K extends keyof T> = Partial<T> & Required<Pick<T, K>>;

/**
 * The return value of Object.keys is always string[], which makes the compiler
 * grumpy. So this is a utility function that calls Object.keys in a TS-friendly
 * way.
 */
export function getKeys<T>(obj: NonNullable<T>) {
  return Object.keys(obj) as Array<keyof T>;
}
